import * as ngCommon from '@angular/common'
import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { capitalize } from '@attain-apps/string-utils'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../../../state'

/**
 * Number of milliseconds to debounce rapid updates to the action query
 *
 * I.e. if multiple updates are made in less than this number of milliseconds,
 * only the last of those updates will actually be applied
 */
const QUERY_UPDATE_DEBOUNCE_MS = 500

@ngCore.Component({
  imports: [
    ngCommon.JsonPipe,
    ds.button.Component,
    ds.input.text.Component,
    ds.input.code.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    ds.overlay.centered.Component,
    ds.tabs.floatingHorizontal.Component,
    ds.tabs.tab.Directive,
  ],
  selector: 'atn-mosaic-dev-action-dialog',
  styleUrls: ['./action.component.scss'],
  templateUrl: './action.component.html',
})
export class AtnActionDialogComponent {
  /** @see {@link AtnMosaicEcService.actionsCollection} */
  public readonly actionsEc = ngCore.inject(state.mosaic.dev.actions.Collection)

  /** @see {@link state.mosaic.dev.actions.plugins.TestingPlugin,} */
  public readonly actionTestingPlugin = this.actionsEc.plugins
    .lookup(state.mosaic.dev.actions.plugins.testing.Plugin)
    .get()

  /** Is focused entity a new entity or existing entity  */
  public readonly isNewSg = ngCore.computed(
    (
      isNew = this.actionsEc.plugins.currentEntityPlugin.feature.selectors
        .isCreating()
        .asSignal(),
    ) => isNew,
  )

  /** Title of the action dialog   */
  public readonly actionTitleSg = ngCore.computed((isNew = this.isNewSg()) => {
    if (isNew) {
      return 'New Action'
    } else {
      // NOTE: not resolving in the arguments list, due to the fact that it
      // might not be needed to be resolved there.
      const action =
        this.actionsEc.plugins.currentEntityPlugin.feature.selectors
          .getEditedEntity()
          .asSignal()
      return action?.name ?? 'Edit Action'
    }
  })

  // TODO: how to get this from the entity collection
  /** Loading indicator */
  public readonly loadingSg = ngCore.computed(() => false)

  /**
   * Used to debounce rapid updates to avoid excessive action dispatches
   */
  private readonly _updatedQuery$ = new rxjs.BehaviorSubject<
    ents.core.mosaic.Action.Query | undefined
  >(undefined)

  constructor() {
    /** @see {@link _updatedQuery$} */
    this._updatedQuery$
      .pipe(
        rxjs.debounceTime(QUERY_UPDATE_DEBOUNCE_MS),
        rxjs.map(query => {
          if (!query) {
            return
          }
          this.updateAction('query', query)
        }),
      )
      .subscribe()
  }

  /**
   * Update action property handler
   */
  public updateAction<K extends keyof ents.core.mosaic.Action.Input>(
    prop: K,
    value: ents.core.mosaic.Action.Input[K],
  ): void {
    this.actionsEc.plugins.editingPlugin.feature.actions.edit
      .from(`Dialog.${capitalize(prop)}.Inputted`)
      .dispatch({
        edit: action => {
          if (!action) {
            return
          }
          action[prop] = value
        },
      })
  }

  /** TODO: */
  public updateActionQuery(query: unknown): void {
    // NOTE: we just hope that it is the correct type
    this._updatedQuery$.next(query as ents.core.mosaic.Action.Query)
  }

  /** TODO: */
  public runClickHandler() {
    const identifier =
      this.actionsEc.plugins.currentEntityPlugin.feature.selectors
        .getId()
        .asSignal()
    const action = this.actionsEc.plugins.currentEntityPlugin.feature.selectors
      .getEditedEntity()
      .asSignal()

    if (identifier && action?.query) {
      this.actionTestingPlugin.feature.actions.render
        .from('Dialog.RunButton.Clicked')
        .dispatch({
          identifier,
          query: action.query,
        })
    }
  }

  /** TODO: */
  public saveClickHandler = () => {
    this.actionsEc.plugins.editingPlugin.feature.actions.submit
      .from('Dialog.SaveButton.Clicked')
      .dispatch({
        current: true,
      })
  }

  /** TODO: */
  public cancelClickHandler = () => {
    this.actionsEc.plugins.editingPlugin.feature.actions.discard
      .from('Dialog.CancelButton.Clicked')
      .dispatch({ current: true })
  }
}
